"use client";

import Image from "next/image";
import { useState } from "react";

export default function AppMockup() {
  const [activeTab, setActiveTab] = useState<"home" | "history" | "profile">(
    "home",
  );

  return (
    <div className="w-[300px] sm:w-[320px] h-[600px] sm:h-[640px] bg-white rounded-[3rem] p-4 shadow-2xl border-[8px] border-[#1a1a1a] relative overflow-hidden flex flex-col mx-auto transition-transform hover:scale-[1.02] duration-500 hover:shadow-orange-500/20">
      {/* Top Bar / Notch */}
      <div className="w-32 h-6 bg-[#1a1a1a] absolute top-0 left-1/2 -translate-x-1/2 rounded-b-2xl z-10"></div>

      {/* App Content */}
      <div className="flex-1 overflow-y-auto pt-8 no-scrollbar relative">
        {activeTab === "home" && (
          <div className="animate-fade-in pb-20">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <div className="text-xs text-gray-500">Welcome back</div>
                <div className="font-bold text-gray-800">OM Jewellers</div>
              </div>
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs">
                🔔
              </div>
            </div>

            {/* Live Price Card */}
            <div className="bg-gradient-to-br from-[#fef3c7] to-[#fffbeb] rounded-2xl p-4 mb-6 shadow-sm border border-orange-100">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">💰</span>
                <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">
                  Today's Live Price
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#C9A227]/20 rounded-lg flex items-center justify-center text-[#6b5a1a]">
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <title>Gold</title>
                        <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-gray-800 text-sm">
                      Gold
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900"><span>₹13,937.27</span><span className="text-sm text-gray-500">/gm</span></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center text-gray-700">
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <title>Silver</title>
                        <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-gray-800 text-sm">
                      Silver
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900"><span>₹226.39</span><span className="text-sm text-gray-500">/gm</span></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Explore Section */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="font-bold text-gray-800">Explore Now</h2>
                <span className="text-amber-600">✦</span>
                <div className="h-[1px] flex-1 bg-orange-100"></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {/* Metal Card */}
                <div className="bg-[#064e3b] aspect-square rounded-2xl p-3 relative overflow-hidden group cursor-pointer hover:opacity-90 transition-opacity shadow-lg shadow-green-900/20">
                  <div className="relative z-10 h-full flex flex-col">
                    <span className="text-white text-xs font-bold leading-tight mb-2">
                      CaratPay
                      <br />
                      Metal
                    </span>
                    <div className="mt-auto flex justify-center">
                      <div className="relative w-16 h-16">
                        <div className="absolute inset-0 bg-yellow-400 rounded-full blur-md opacity-20"></div>
                        <span className="text-3xl relative z-10">🥇</span>
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                </div>
                {/* Savings Plan Card */}
                <div className="bg-[#1e3a8a] aspect-square rounded-2xl p-3 relative overflow-hidden group cursor-pointer hover:opacity-90 transition-opacity shadow-lg shadow-blue-900/20">
                  <div className="relative z-10 h-full flex flex-col">
                    <span className="text-white text-xs font-bold leading-tight mb-2">
                      CaratPay
                      <br />
                      Savings
                      <br />
                      Plan
                    </span>
                    <div className="mt-auto flex justify-center">
                      <div className="relative w-16 h-16">
                        <div className="absolute inset-0 bg-blue-400 rounded-full blur-md opacity-20"></div>
                        <span className="text-3xl relative z-10">🏦</span>
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 
          Using negative margin (-mt-8) to "crop" the top status bar from the screenshots
          so they look integrated into the mockup frame.
        */}
        {activeTab === "history" && (
          <div className="animate-fade-in -mt-8">
            <Image
              src="/transactions.png"
              alt="Transactions History"
              width={320}
              height={640}
              className="w-full h-auto object-cover mt-8"
            />
          </div>
        )}

        {activeTab === "profile" && (
          <div className="animate-fade-in -mt-8">
            <Image
              src="/Profile.png"
              alt="User Profile"
              width={320}
              height={640}
              className="w-full h-auto object-cover mt-2"
            />
          </div>
        )}
      </div>

      {/* Bottom Nav */}
      <div className="border-t border-gray-100 p-2 flex justify-around items-center bg-white relative z-20">
        <button
          type="button"
          onClick={() => setActiveTab("home")}
          className={`flex flex-col items-center transition-colors duration-200 cursor-pointer ${activeTab === "home" ? "text-[#6b5a1a]" : "text-gray-500 hover:text-gray-700"}`}
        >
          <svg
            className="w-5 h-5"
            fill={activeTab === "home" ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth={activeTab === "home" ? 0 : 2}
            viewBox="0 0 24 24"
          >
            <title>Home</title>
            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
          </svg>
          <span
            className={`text-[10px] ${activeTab === "home" ? "font-bold" : "font-medium"}`}
          >
            Home
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("history")}
          className={`flex flex-col items-center transition-colors duration-200 cursor-pointer ${activeTab === "history" ? "text-[#6b5a1a]" : "text-gray-500 hover:text-gray-700"}`}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <title>History</title>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span
            className={`text-[10px] ${activeTab === "history" ? "font-bold" : "font-medium"}`}
          >
            History
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("profile")}
          className={`flex flex-col items-center transition-colors duration-200 cursor-pointer ${activeTab === "profile" ? "text-[#6b5a1a]" : "text-gray-500 hover:text-gray-700"}`}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <title>Profile</title>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          <span
            className={`text-[10px] ${activeTab === "profile" ? "font-bold" : "font-medium"}`}
          >
            Profile
          </span>
        </button>
      </div>
    </div>
  );
}
